import {
    colorBehavior,
    createFeatureCollection,
    getSelectedFeaturesLineWidthExpression,
    getLayerColorForExpression,
    isDirectionsLayerById,
    isLabelsLayerById,
    lineBlurExpression,
    lineDashArrayExpression,
    lineWidthExpression,
    pointStrokeBehavior,
} from '@utils/mapUtils';
import { MapConfigLegendProperties } from '@api/entities/replancity_MapConfigLegendProperties';
import {
    DateType,
    MapboxGlLayersType,
    Sources,
    DeckGlLayersType,
    LayerVisibility,
    LayerTypes,
    LayerType,
    SourceItem,
    LngLat,
} from './types';
import { ViewPortCoordinates } from '@api/entities/local/Borders';
import { DEFAULT_MAP_BOUNDS_COORDINATES } from '@utils/constants';
import { MapLayerDistributionType } from '@api/entities/replancity_MapLayer';
import { SimulationState } from '@api/entities/replancity_RunnedAlgorithm';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectedFeatureProperties } from '../showInfo/types';
import { arrayMove } from '@dnd-kit/sortable';
import { BBox } from 'geojson';
import { addLayer, createSource } from '@redux/map/utils';
import mapboxgl from 'mapbox-gl';
import { ColorForLayerExpression } from '@utils/types';

export type MapStoreState = {
    sources: Sources;
    mapboxglLayers: MapboxGlLayersType;
    deckglLayers: DeckGlLayersType;
    layersGroups: string[];
    allLayersIdsBySourceId: Record<string, string[]>;
    dataBySourceId: Record<string, GeoJSON.FeatureCollection>;
    legendsBySourceId: Record<string, MapConfigLegendProperties>;
    distributionsById: Record<string, MapLayerDistributionType & { layerName: string }>;
    dateTypesById: Record<string, DateType>;
    calculationStateBySourceId: Record<string, SimulationState>;
    viewPortCoordinates: ViewPortCoordinates;
    isViewPortFixed: boolean;
    flyToPoint: LngLat & { zoom?: number };
    boundsToFit: BBox | null;
    selectedDateTypeId: string;
    redirectOnFeatureSelection: boolean;
    requireToHighlightFeatures: SelectedFeatureProperties[];
    requireToReloadSources: Record<string, boolean>;
    mapConfigReloadRequired: boolean;
};

const initialState: MapStoreState = {
    sources: { byId: {}, allId: [] },
    mapboxglLayers: {
        byId: {},
        allId: [],
    },
    deckglLayers: {
        byId: {},
        allId: [],
    },
    layersGroups: [],
    allLayersIdsBySourceId: {},
    dataBySourceId: {},
    legendsBySourceId: {},
    distributionsById: {},
    dateTypesById: {},
    calculationStateBySourceId: {},
    viewPortCoordinates: DEFAULT_MAP_BOUNDS_COORDINATES,
    isViewPortFixed: false,
    flyToPoint: { lng: 0, lat: 0, zoom: DEFAULT_MAP_BOUNDS_COORDINATES.zoom },
    boundsToFit: null,
    selectedDateTypeId: '',
    redirectOnFeatureSelection: true,
    requireToHighlightFeatures: [],
    requireToReloadSources: {},
    mapConfigReloadRequired: false,
};

const getHighlightedMapboxglLayersById = ({
    layerId,
    type,
    shouldHighlight,
    expression,
    isClickableLayer,
}: {
    layerId: string;
    type: any;
    isClickableLayer: boolean;
    lockedColor: ColorForLayerExpression['lockedColor'];
} & (
    | {
          shouldHighlight: boolean;
          expression?: mapboxgl.Expression;
      }
    | { shouldHighlight?: never; expression?: never }
)) => {
    return {
        ...(type === 'line'
            ? {
                  'line-width':
                      shouldHighlight && expression
                          ? getSelectedFeaturesLineWidthExpression(expression)
                          : lineWidthExpression,
                  'line-opacity':
                      shouldHighlight && expression
                          ? ['case', expression, 1, 0.3]
                          : isClickableLayer
                            ? 1
                            : 0.3,
                  'line-blur': lineBlurExpression,
                  'line-dasharray': lineDashArrayExpression,
                  ...getLayerColorForExpression({
                      layerProperty: 'line-color',
                      expression,
                      areFeaturesSelected: !!shouldHighlight && !!expression,
                      highLightColor: colorBehavior,
                      regularColor: 'grey',
                      isClickableLayer,
                      lockedColor: 'grey',
                  }),
              }
            : type === 'circle'
              ? {
                    'circle-opacity': shouldHighlight
                        ? ['case', expression, 1, 0.5]
                        : isClickableLayer
                          ? 1
                          : 0.5,
                    ...getLayerColorForExpression({
                        layerProperty: 'circle-color',
                        expression,
                        areFeaturesSelected: !!(shouldHighlight && expression),
                        highLightColor: colorBehavior,
                        regularColor: 'grey',
                        isClickableLayer,
                        lockedColor: 'grey',
                    }),
                    'circle-radius': lineWidthExpression,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': pointStrokeBehavior,
                }
              : type === 'fill'
                ? {
                      'fill-opacity': shouldHighlight
                          ? ['case', expression, 1, 0.5]
                          : isClickableLayer
                            ? 1
                            : 0.5,
                      ...getLayerColorForExpression({
                          layerProperty: 'fill-color',
                          expression,
                          areFeaturesSelected: !!(shouldHighlight && expression),
                          highLightColor: colorBehavior,
                          regularColor: 'grey',
                          isClickableLayer,
                          lockedColor: 'grey',
                      }),
                  }
                : type === 'symbol'
                  ? {
                        ...(isLabelsLayerById(layerId)
                            ? getLayerColorForExpression({
                                  layerProperty: 'text-color',
                                  expression,
                                  areFeaturesSelected: !!(shouldHighlight && expression),
                                  highLightColor: colorBehavior,
                                  regularColor: '#fff',
                                  isClickableLayer,
                                  lockedColor: 'grey',
                              })
                            : null),
                        ...(isDirectionsLayerById(layerId)
                            ? getLayerColorForExpression({
                                  layerProperty: 'text-color',
                                  expression,
                                  areFeaturesSelected: !!(shouldHighlight && expression),
                                  highLightColor: '#fff',
                                  regularColor: '#fff',
                                  isClickableLayer,
                                  lockedColor: 'grey',
                              })
                            : null),
                    }
                  : {}),
    };
};

const mapSlice = createSlice({
    name: 'map',
    initialState,
    reducers: {
        addedSources(state, action: PayloadAction<SourceItem | SourceItem[]>) {
            if (Array.isArray(action.payload)) {
                for (const source of action.payload) {
                    createSource(state, source);
                }
            } else {
                createSource(state, action.payload);
            }
        },
        removedSource(state, action: PayloadAction<string>) {
            const sourceId = action.payload;
            const source = state.sources.byId[sourceId];
            const layerIds = state.allLayersIdsBySourceId[sourceId] ?? [];

            delete state.sources.byId[sourceId];
            state.sources.allId = state.sources.allId.filter(id => id !== sourceId);

            layerIds.forEach(layerId => {
                delete state.mapboxglLayers.byId[layerId];
                delete state.deckglLayers.byId[layerId];
            });
            state.mapboxglLayers.allId = state.mapboxglLayers.allId.filter(
                id => !layerIds.includes(id)
            );
            state.deckglLayers.allId = state.deckglLayers.allId.filter(
                id => !layerIds.includes(id)
            );

            delete state.allLayersIdsBySourceId[sourceId];
            delete state.dataBySourceId[sourceId];
            delete state.legendsBySourceId[sourceId];
            delete state.distributionsById[sourceId];
            delete state.calculationStateBySourceId[sourceId];
            delete state.requireToReloadSources[sourceId];

            if (source?.group) {
                const hasGroupSources = state.sources.allId.some(
                    id => state.sources.byId[id]?.group === source.group
                );

                if (!hasGroupSources) {
                    state.layersGroups = state.layersGroups.filter(group => group !== source.group);
                }
            }
        },
        addedSourceData(
            state,
            action: PayloadAction<{
                sourceId: string;
                data: GeoJSON.FeatureCollection;
                legend?: MapConfigLegendProperties | null;
                distribution?: (MapLayerDistributionType & { layerName: string }) | null;
            }>
        ) {
            const { sourceId, legend, distribution, data } = action.payload;
            // const {type, features} = data ?? {type: 'FeatureCollection', features: []};

            state.dataBySourceId = {
                ...state.dataBySourceId,
                [sourceId]: data,
            };
            if (legend) {
                state.legendsBySourceId[sourceId] = legend;
            }
            if (distribution) {
                state.distributionsById[sourceId] = distribution;
            }
        },
        clearAllSourcesData(state) {
            state.dataBySourceId = {};
        },
        clearSourceData(state, action: PayloadAction<string>) {
            state.dataBySourceId[action.payload] = createFeatureCollection();
        },
        addedLayers(
            state,
            action: PayloadAction<{
                layers: LayerTypes | LayerTypes[];
                position?: 'top' | 'bottom';
            }>
        ) {
            const { layers, position = 'bottom' } = action.payload;

            if (Array.isArray(layers)) {
                for (const layer of layers) {
                    addLayer(state, layer, position);
                }
            } else {
                addLayer(state, layers, position);
            }
        },
        updatedMapboxLayersOrder(
            state,
            action: PayloadAction<{ oldIndex: number; newIndex: number }>
        ) {
            const { oldIndex, newIndex } = action.payload;
            state.mapboxglLayers.allId = arrayMove(state.mapboxglLayers.allId, oldIndex, newIndex);
        },
        addedDateTypesByIds(state, action: PayloadAction<Record<string, any>>) {
            state.dateTypesById = action.payload;
        },
        setViewportCoordinates(
            state,
            action: PayloadAction<{
                viewPortCoordinates: ViewPortCoordinates;
                isViewPortFixed?: boolean;
            }>
        ) {
            const { isViewPortFixed = false, viewPortCoordinates } = action.payload;
            state.viewPortCoordinates = viewPortCoordinates;
            state.isViewPortFixed = isViewPortFixed;
        },
        setZoom(state, action: PayloadAction<number>) {
            const { viewPortCoordinates } = state;
            state.viewPortCoordinates = {
                ...viewPortCoordinates,
                zoom: action.payload,
            };
        },
        setMapConfigReloadRequired(state, action: PayloadAction<boolean>) {
            state.mapConfigReloadRequired = action.payload;
        },
        changedLayerVisibility(
            state,
            action: PayloadAction<{
                layerId: string;
                visible: boolean;
            }>
        ) {
            const { layerId, visible } = action.payload;
            const layer = state.mapboxglLayers.byId[layerId] ?? state.deckglLayers.byId[layerId];

            if (layer) {
                layer.layout.visibility = visible ? LayerVisibility.VISIBLE : LayerVisibility.NONE;
            }
        },
        setLayerLoading(
            state,
            action: PayloadAction<{
                layerId: string;
                loading: boolean;
                loaded?: boolean;
            }>
        ) {
            const { layerId, loading, loaded } = action.payload;
            if (state[LayerType.MAPBOXGL].byId[layerId]) {
                state[LayerType.MAPBOXGL].byId[layerId].loading = loading;
                if (loaded) {
                    state[LayerType.MAPBOXGL].byId[layerId].loaded = loaded;
                }
            } else if (state[LayerType.DECKGL].byId[layerId]) {
                state[LayerType.DECKGL].byId[layerId].loading = loading;
                if (loaded) {
                    state[LayerType.DECKGL].byId[layerId].loaded = loaded;
                }
            }
        },
        clearedMapData(state, action: PayloadAction) {
            const {
                redirectOnFeatureSelection,
                requireToHighlightFeatures,
                mapConfigReloadRequired,
                ...restState
            } = initialState;
            return {
                ...state,
                ...restState,
                viewPortCoordinates: state.viewPortCoordinates,
                sources: { byId: {}, allId: [] },
            };
        },
        selectedDateType(state, action: PayloadAction<string>) {
            state.selectedDateTypeId = action.payload;
        },
        addedLayerSimulationState(
            state,
            action: PayloadAction<{ layerId: string; calculationState: string }>
        ) {
            const { layerId, calculationState } = action.payload;
            state.calculationStateBySourceId[layerId] = calculationState as SimulationState;
        },
        setSourceReloadRequired(
            state,
            action: PayloadAction<{ sourceId: string; isReloadRequired: boolean }>
        ) {
            const { sourceId, isReloadRequired } = action.payload;
            return {
                ...state,
                requireToReloadSources: {
                    ...state.requireToReloadSources,
                    [sourceId]: isReloadRequired,
                },
            };
        },
        enabledLayersInteractivity(
            state,
            action: PayloadAction<{ layersClickabilityById: Record<string, boolean> }>
        ) {
            state.mapboxglLayers.byId = state.mapboxglLayers.allId.reduce((acc, id) => {
                acc[id] = {
                    ...state.mapboxglLayers.byId[id],
                    paint: {
                        ...state.mapboxglLayers.byId[id].paint,
                        ...(getHighlightedMapboxglLayersById({
                            layerId: id,
                            type: state.mapboxglLayers.byId[id].type,
                            shouldHighlight: true,
                            isClickableLayer: action.payload.layersClickabilityById[id],
                            lockedColor: 'grey',
                        }) as any),
                    },
                    clickable: action.payload.layersClickabilityById[id],
                };
                return acc;
            }, {});
        },
        disabledAllLayersInteractivity(state) {
            state.mapboxglLayers.byId = state.mapboxglLayers.allId.reduce((acc, id) => {
                acc[id] = {
                    ...state.mapboxglLayers.byId[id],
                    paint: {
                        ...state.mapboxglLayers.byId[id].paint,
                        ...(getHighlightedMapboxglLayersById({
                            layerId: id,
                            type: state.mapboxglLayers.byId[id].type,
                            shouldHighlight: false,
                            isClickableLayer: false,
                            lockedColor: 'grey',
                        }) as any),
                    },
                    clickable: false,
                };
                return acc;
            }, {});
        },
        disabledLayersInteractivityExcept(state, action: PayloadAction<string>) {
            const entityName = action.payload;
            state.mapboxglLayers.byId = state.mapboxglLayers.allId.reduce((acc, id) => {
                const source = state.sources.byId[id];
                acc[id] = {
                    ...state.mapboxglLayers.byId[id],
                    paint: {
                        ...state.mapboxglLayers.byId[id].paint,
                        ...(getHighlightedMapboxglLayersById({
                            layerId: id,
                            type: state.mapboxglLayers.byId[id].type,
                            shouldHighlight: source?.entityName === entityName,
                            isClickableLayer: source?.entityName === entityName,
                            lockedColor: 'grey',
                        }) as any),
                    },
                    clickable: source?.entityName === entityName, // some sources don't have entityName in mapConfig layers
                };
                return acc;
            }, {});
        },
        highlightLayersPaint(
            state,
            action: PayloadAction<{
                layerId: string;
                featureStateIds?: string[];
            }>
        ) {
            const { layerId, featureStateIds = [] } = action.payload;

            if (!layerId || !state.mapboxglLayers.byId[layerId]) {
                return state;
            }

            const expression: mapboxgl.Expression = [
                'in',
                ['to-string', ['get', 'featureStateId']],
                ['literal', featureStateIds?.length ? featureStateIds.map(String) : []],
            ];

            const { type } = state.mapboxglLayers.byId[layerId];
            state.mapboxglLayers.byId = state.mapboxglLayers.allId.reduce((acc, id) => {
                if (id === layerId) {
                    acc[id] = {
                        ...state.mapboxglLayers.byId[layerId],
                        paint: {
                            ...state.mapboxglLayers.byId[layerId].paint,
                            ...getHighlightedMapboxglLayersById({
                                layerId,
                                type,
                                shouldHighlight: !!featureStateIds?.length,
                                expression,
                                isClickableLayer: state.mapboxglLayers.byId[layerId].clickable,
                                lockedColor: 'grey',
                            }),
                        },
                    };
                } else {
                    acc[id] = state.mapboxglLayers.byId[id];
                }

                return acc;
            }, {});
        },
        changedLayerInteractivity(
            state,
            action: PayloadAction<{ layersIds: string[]; isInteractive: boolean }>
        ) {
            const { layersIds, isInteractive } = action.payload;
            layersIds.forEach(layerId => {
                const layer = state.mapboxglLayers.byId[layerId];
                const { type } = layer;

                if (layer) {
                    layer.clickable = isInteractive;
                    layer.paint = {
                        ...layer.paint,
                        ...(getHighlightedMapboxglLayersById({
                            layerId,
                            type,
                            shouldHighlight: isInteractive,
                            isClickableLayer: isInteractive,
                            lockedColor: 'grey',
                        }) as any),
                    };
                    state.mapboxglLayers.byId[layerId] = { ...layer };
                }
            });

            state = {
                ...state,
                mapboxglLayers: {
                    ...state.mapboxglLayers,
                    byId: {
                        ...state.mapboxglLayers.byId,
                    },
                },
            };
        },
        flewToPoint(
            state,
            action: PayloadAction<{ lng: number; lat: number; zoom?: number } | undefined>
        ) {
            state.flyToPoint = action.payload ?? initialState.flyToPoint;
        },
        setBoundsToFit(state, action: PayloadAction<BBox | undefined>) {
            state.boundsToFit = action.payload ?? initialState.boundsToFit;
        },
        requireToHighlightFeatures(
            state,
            action: PayloadAction<{
                featureProperties: SelectedFeatureProperties | SelectedFeatureProperties[];
                restorePreviouslyHighlighted?: boolean;
            }>
        ) {
            const { featureProperties, restorePreviouslyHighlighted } = action.payload;
            state.requireToHighlightFeatures = [
                ...(restorePreviouslyHighlighted ? state.requireToHighlightFeatures : []),
                ...(Array.isArray(featureProperties) ? featureProperties : [featureProperties]),
            ] as any;
        },
        removedFeaturesHighlighting(state, action: PayloadAction) {
            return {
                ...state,
                requireToHighlightFeatures: [],
            };
        },
        setRedirectOnFeatureSelection(state, action: PayloadAction<boolean>) {
            state.redirectOnFeatureSelection = action.payload;
        },
    },
});

export const {
    addedSources,
    removedSource,
    addedSourceData,
    clearAllSourcesData,
    clearSourceData,
    addedLayers,
    updatedMapboxLayersOrder,
    addedDateTypesByIds,
    setViewportCoordinates,
    setZoom,
    setMapConfigReloadRequired,
    changedLayerVisibility,
    setLayerLoading,
    clearedMapData,
    selectedDateType,
    addedLayerSimulationState,
    setSourceReloadRequired,
    enabledLayersInteractivity,
    disabledAllLayersInteractivity,
    disabledLayersInteractivityExcept,
    changedLayerInteractivity,
    highlightLayersPaint,
    flewToPoint,
    setBoundsToFit,
    requireToHighlightFeatures,
    removedFeaturesHighlighting,
    setRedirectOnFeatureSelection,
} = mapSlice.actions;

export default mapSlice.reducer;
